<template>
  <img v-bind="props" :src="source" :srcset="srcSet" :alt="props.alt || ''" />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ImgHTMLAttributes } from 'vue/types/jsx'
import { extractFirstVideoFrame } from './extractFirstVideoFrame'
import { getSrcSet } from './getSrcSet'

interface Props extends ImgHTMLAttributes {
  src?: string

  /**
   * Add the "sizes" attribute to enable optimization.
   * It defines the visual size of the image element on the page.
   * Docs: https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images#resolution_switching_different_sizes
   *
   * Examples:
   * <OptimizedImage :src="image" sizes="45px" />
   * <OptimizedImage :src="image" sizes="630px" />
   * <OptimizedImage :src="image" sizes="33vw" />
   **/
  sizes?: string
}

const props = defineProps<Props>()

const source = ref(props.src)

const srcSet = computed(() => {
  return getSrcSet(source.value)
})

const getSource = async () => {
  if (source.value?.includes('.mp4')) {
    source.value = await extractFirstVideoFrame(source.value)
  }
}

watch(
  () => props.src,
  (newSrc) => {
    source.value = newSrc
    getSource()
  },
  { immediate: true }
)
</script>
